// Wettbewerbsmonitoring-Tab (Lastenheft Abschnitte 13–15: Dashboard, Filter, Fallverwaltung).
// Zeigt Beobachtungen, keine rechtlichen Bewertungen — das steht auch sichtbar im Panel.

interface Fall {
  fallnummer: string; status: string; schweregrad: string; punkte: number;
  kategorie: string; aussage: string; url: string; erstfeststellung: string;
  wiedervorlage: string | null; domain: string; name: string;
  frist_am?: string | null; frist_pruefung_am?: string | null; rueckmeldung_faellig_am?: string | null;
  einstufung?: string | null; eindeutigkeit?: string | null;
  begruendung?: string | null; formulierung?: string | null; fehlende_nachweise?: string | null;
}
interface WbResponse {
  bereit: boolean;
  kennzahlen?: Record<string, number>;
  faelle?: Fall[];
  topWettbewerber?: { domain: string; name: string; sichtbarkeit: number | null; faelle: number }[];
  kategorien?: { kategorie: string; n: number }[];
}
interface Anbieter {
  id: number; domain: string; name: string | null; bereich: string | null;
  sichtbarkeit: number | null; aktiv: number; notiz: string | null; angelegt_am: string;
  impressum_url: string | null; impressum_land: string | null;
  impressum_inhaber: string | null; impressum_geprueft_am: string | null;
  urls: number; geprueft: number; faelle: number;
}

const esc = (s: string): string =>
  String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]!));

const EINSTUFUNG: Record<string, { symbol: string; text: string }> = {
  tatsachenbehauptung: { symbol: "🔴", text: "Tatsachenbehauptung" },
  pflichtinformation: { symbol: "🟠", text: "Pflichtangabe fehlt" },
  gesamteindruck: { symbol: "🟡", text: "Gesamteindruck" },
  vermutlich_zulaessig: { symbol: "⚪", text: "vermutlich zulässig" },
};
const STATUS_TEXT: Record<string, string> = {
  neu_erkannt: "neu erkannt", manuell_pruefen: "manuell prüfen", beweise_vollstaendig: "Beweise vollständig",
  freigegeben: "freigegeben", eingereicht: "eingereicht", in_pruefung: "in Prüfung", beanstandet: "beanstandet",
  geaendert: "Wettbewerber hat geändert", erledigt: "erledigt", kein_verstoss: "kein Verstoß", beobachten: "Beobachtung",
};

export function setupWettbewerb(deps: {
  api: <T>(path: string, method?: "DELETE" | "GET" | "PATCH" | "POST" | "PUT", body?: unknown)
    => Promise<{ status: number; data: T | null }>;
  formatDate: (s: string) => string;
}): void {
  // Das Marketing-Fenster wird erst beim Öffnen aus einer <template> erzeugt — das Panel
  // existiert beim Seitenstart noch nicht. Deshalb hier delegiert am Dokument arbeiten.
  let panel: HTMLElement | null = null;
  let host: HTMLElement | null = null;
  let alleFaelle: Fall[] = [];
  let alleAnbieter: Anbieter[] = [];
  let ansicht: "anbieter" | "faelle" = "faelle";

  const finden = (): boolean => {
    panel = document.querySelector<HTMLElement>('[data-tab-panel="wettbewerb"]');
    host = panel?.querySelector<HTMLElement>("[data-wb-inhalt]") ?? null;
    return !!host;
  };

  const kachel = (wert: number | string, label: string, klasse = ""): string =>
    `<div class="om-kpi ${klasse}"><strong>${wert}</strong><span>${esc(label)}</span></div>`;

  function zeichneFaelle(): void {
    const liste = host!.querySelector<HTMLElement>("[data-wb-faelle]");
    if (!liste) return;
    const fEin = (host!.querySelector("[data-wb-filter-einstufung]") as HTMLSelectElement)?.value || "";
    const fStatus = (host!.querySelector("[data-wb-filter-status]") as HTMLSelectElement)?.value || "";
    const suche = ((host!.querySelector("[data-wb-suche]") as HTMLInputElement)?.value || "").toLowerCase();

    const gefiltert = alleFaelle.filter((f) =>
      (!fEin || f.einstufung === fEin) && (!fStatus || f.status === fStatus)
      && (!suche || `${f.domain} ${f.aussage} ${f.kategorie} ${f.name}`.toLowerCase().includes(suche)));

    if (!gefiltert.length) { liste.innerHTML = '<p class="dim">Keine Fälle für diese Auswahl.</p>'; return; }
    liste.innerHTML = gefiltert.map((f) => {
      const e = EINSTUFUNG[f.einstufung ?? ""] ?? { symbol: "·", text: "noch nicht bewertet" };
      return `<div class="om-wb-fall" data-fall="${esc(f.fallnummer)}">
        <div class="om-wb-kopf">
          <span class="om-wb-symbol" title="${esc(e.text)}">${e.symbol}</span>
          <b>${esc(f.fallnummer)}</b>
          <span class="om-wb-domain">${esc(f.domain)}</span>
          <span class="om-wb-kat">${esc(f.kategorie)}</span>
          <span class="om-wb-punkte">${f.punkte} P.</span>
          <span class="om-wb-status">${esc(STATUS_TEXT[f.status] ?? f.status)}</span>
        </div>
        <div class="om-wb-aussage">„${esc(f.aussage)}"</div>
        ${f.begruendung ? `<div class="om-wb-begr">${esc(f.begruendung)}</div>` : ""}
        ${f.fehlende_nachweise ? `<div class="om-wb-fehlt"><b>Noch zu belegen:</b> ${esc(f.fehlende_nachweise)}</div>` : ""}
        ${f.frist_am || f.rueckmeldung_faellig_am ? `<div class="om-wb-frist">${
          [f.frist_am ? `Frist der Wettbewerbszentrale: <b>${esc(f.frist_am)}</b>${f.frist_pruefung_am ? ` · Nachprüfung am ${esc(f.frist_pruefung_am)}` : ""}` : "",
           f.rueckmeldung_faellig_am && !f.frist_am ? `Antwort erwartet bis <b>${esc(f.rueckmeldung_faellig_am)}</b>` : ""]
          .filter(Boolean).join(" &nbsp;|&nbsp; ")}</div>` : ""}
        <div class="om-wb-fuss">
          <a href="${esc(f.url)}" target="_blank" rel="noopener noreferrer">Fundstelle öffnen</a>
          <button class="btn-link" data-act="vorlage" data-fall="${esc(f.fallnummer)}">Einreichungstext</button>
          <button class="btn-link" data-act="anschreiben" data-fall="${esc(f.fallnummer)}"
             title="Betreff und Anschreiben für die Wettbewerbszentrale in die Zwischenablage kopieren">Anschreiben</button>
          <a class="btn-link" href="/api/marketing/wettbewerb/pdf?fall=${encodeURIComponent(f.fallnummer)}"
             download title="Nur dieser eine Sachverhalt — für Rückfragen zu einem Punkt">Einzel-PDF</a>
          <a class="btn-link" href="/api/marketing/wettbewerb/dossier?domain=${encodeURIComponent(f.domain)}"
             download title="Alle Feststellungen zu diesem Anbieter in einem Dokument — das Format für die Ersteinreichung">Dossier</a>
          <button class="btn-link" data-act="dossier-brief" data-domain="${esc(f.domain)}"
             title="Anschreiben zum Gesamtdossier für die Wettbewerbszentrale">Dossier-Anschreiben</button>
          <button class="btn-link" data-act="frist" data-fall="${esc(f.fallnummer)}"
             title="Frist der Wettbewerbszentrale eintragen und Nachprüfung terminieren">Frist</button>
          <select data-act="status" data-fall="${esc(f.fallnummer)}" aria-label="Status ändern">
            <option value="">Status ändern …</option>
            ${Object.entries(STATUS_TEXT).map(([k, v]) => `<option value="${k}"${k === f.status ? " selected" : ""}>${esc(v)}</option>`).join("")}
          </select>
          <span class="dim">seit ${esc(deps.formatDate(f.erstfeststellung))}</span>
        </div>
      </div>`;
    }).join("");
  }

  // Für eine Einreichung zählt, wer greifbar ist: ohne deutsches Impressum trägt die
  // Wettbewerbszentrale einen Fall in aller Regel nicht weiter (Uli, 31.07.2026).
  const LAND: Record<string, { symbol: string; text: string }> = {
    "DE": { symbol: "🇩🇪", text: "deutsches Impressum — verfolgbar" },
    "DE?": { symbol: "🇩🇪?", text: "vermutlich Deutschland — bitte nachsehen" },
    "AT": { symbol: "🇦🇹", text: "Österreich — für eine Einreichung kaum geeignet" },
    "CH": { symbol: "🇨🇭", text: "Schweiz — für eine Einreichung kaum geeignet" },
    "KEINS": { symbol: "⛔", text: "kein Impressum gefunden — kein Adressat feststellbar" },
  };

  function zeichneAnbieter(): void {
    const liste = host!.querySelector<HTMLElement>("[data-wb-anbieter]");
    if (!liste) return;
    const suche = ((host!.querySelector("[data-wb-anbieter-suche]") as HTMLInputElement)?.value || "").toLowerCase();
    const fLand = (host!.querySelector("[data-wb-anbieter-land]") as HTMLSelectElement)?.value || "";

    const gefiltert = alleAnbieter.filter((a) => {
      if (suche && !`${a.domain} ${a.name ?? ""} ${a.bereich ?? ""} ${a.impressum_inhaber ?? ""}`.toLowerCase().includes(suche)) return false;
      if (fLand === "de" && !(a.impressum_land === "DE" || a.impressum_land === "DE?")) return false;
      if (fLand === "nichtde" && (a.impressum_land === "DE" || a.impressum_land === "DE?" || !a.impressum_land)) return false;
      if (fLand === "ungeprueft" && a.impressum_geprueft_am) return false;
      if (fLand === "inaktiv" && a.aktiv) return false;
      return true;
    });

    liste.innerHTML = `<p class="dim">${gefiltert.length} von ${alleAnbieter.length} Anbietern</p>
      <table class="om-tab om-wb-anbietertab">
        <thead><tr><th>Domain</th><th>Impressum</th><th>Betreiber</th><th>Seiten</th><th>Fälle</th><th></th></tr></thead>
        <tbody>${gefiltert.map((a) => {
    const l = LAND[a.impressum_land ?? ""] ?? { symbol: "·", text: "noch nicht geprüft" };
    return `<tr${a.aktiv ? "" : ' class="dim"'}>
            <td><a href="https://${esc(a.domain)}/" target="_blank" rel="noopener noreferrer">${esc(a.domain)}</a>
              ${a.aktiv ? "" : ' <span class="dim">(stillgelegt)</span>'}</td>
            <td title="${esc(l.text)}">${l.symbol} ${esc(a.impressum_land ?? "—")}</td>
            <td>${esc(a.impressum_inhaber ?? "—")}</td>
            <td>${a.geprueft}/${a.urls}</td>
            <td>${a.faelle || "—"}</td>
            <td class="om-wb-anbieter-akt">
              ${a.faelle ? `<a class="btn-link" href="/api/marketing/wettbewerb/dossier?domain=${encodeURIComponent(a.domain)}"
                 download title="Alle ${a.faelle} Feststellungen dieses Anbieters in einem Dokument — mit Bildnachweisen, Prüfsummen und Zeitstempel">Dossier</a>` : ""}
              <button class="btn-link" data-act="wb-scan" data-domain="${esc(a.domain)}"
                 title="Seiten dieses Anbieters neu einsammeln und Impressum prüfen">prüfen</button>
              <button class="btn-link" data-act="wb-aktiv" data-id="${a.id}" data-aktiv="${a.aktiv ? 0 : 1}"
                 >${a.aktiv ? "stilllegen" : "wieder aufnehmen"}</button>
            </td></tr>`;
  }).join("")}</tbody></table>`;
  }

  function zeichneAnsicht(): void {
    const f = host!.querySelector<HTMLElement>("[data-wb-bereich-faelle]");
    const a = host!.querySelector<HTMLElement>("[data-wb-bereich-anbieter]");
    if (f) f.hidden = ansicht !== "faelle";
    if (a) a.hidden = ansicht !== "anbieter";
    host!.querySelectorAll<HTMLElement>("[data-wb-ansicht]").forEach((b) => {
      b.classList.toggle("aktiv", b.dataset.wbAnsicht === ansicht);
    });
  }

  async function anbieterLaden(): Promise<void> {
    const r = await deps.api<{ bereit: boolean; liste?: Anbieter[] }>("/api/marketing/wettbewerb/wettbewerber");
    alleAnbieter = r.data?.liste ?? [];
    zeichneAnbieter();
  }

  async function laden(): Promise<void> {
    host!.innerHTML = '<p class="dim">Lade …</p>';
    const r = await deps.api<WbResponse>("/api/marketing/wettbewerb");
    if (r.status !== 200 || !r.data) { host!.innerHTML = `<p class="dim">Konnte nicht laden (Fehler ${r.status}).</p>`; return; }
    if (!r.data.bereit) { host!.innerHTML = '<p class="dim">Das Wettbewerbsmonitoring ist noch nicht eingerichtet.</p>'; return; }

    const k = r.data.kennzahlen ?? {};
    alleFaelle = r.data.faelle ?? [];
    host!.innerHTML = `
      <div class="om-kpis">
        ${kachel(k.wettbewerber ?? 0, "Wettbewerber")}
        ${kachel(`${k.geprueft ?? 0}/${k.urls ?? 0}`, "Seiten geprüft")}
        ${kachel(k.fundstellen ?? 0, "Fundstellen")}
        ${kachel(k.faelle ?? 0, "Fälle")}
        ${kachel(k.belastbar ?? 0, "belastbar", "warn")}
        ${kachel(k.offen ?? 0, "offen")}
        ${kachel(k.eingereicht ?? 0, "eingereicht")}
        ${kachel(k.wiedervorlage ?? 0, "Nachkontrolle fällig")}
      </div>
      <p class="dim om-wb-hinweis">Alle Angaben sind automatisierte Beobachtungen öffentlich zugänglicher Seiten.
        Sie enthalten keine rechtliche Bewertung — diese trifft ausschließlich die Wettbewerbszentrale.</p>

      <div class="om-wb-ansichten">
        <button class="btn-link" data-wb-ansicht="faelle">Fälle</button>
        <button class="btn-link" data-wb-ansicht="anbieter">Wettbewerber</button>
      </div>

      <div data-wb-bereich-faelle>
        <div class="om-wb-filter">
          <select data-wb-filter-einstufung aria-label="Einstufung">
            <option value="">alle Einstufungen</option>
            ${Object.entries(EINSTUFUNG).map(([k2, v]) => `<option value="${k2}">${v.symbol} ${esc(v.text)}</option>`).join("")}
          </select>
          <select data-wb-filter-status aria-label="Status">
            <option value="">alle Status</option>
            ${Object.entries(STATUS_TEXT).map(([k2, v]) => `<option value="${k2}">${esc(v)}</option>`).join("")}
          </select>
          <input type="search" data-wb-suche placeholder="Suche in Domain, Aussage, Kategorie …" aria-label="Suche">
        </div>
        <div data-wb-faelle class="om-wb-liste"></div>
      </div>

      <div data-wb-bereich-anbieter hidden>
        <form class="om-wb-neu" data-wb-neu>
          <input type="text" name="domain" placeholder="neue Domain oder volle Adresse einfügen …"
                 aria-label="Domain des neuen Wettbewerbers" required>
          <input type="text" name="notiz" placeholder="Notiz, z. B. wo entdeckt (optional)" aria-label="Notiz">
          <button type="submit">aufnehmen</button>
          <span class="dim" data-wb-neu-status></span>
        </form>
        <div class="om-wb-filter">
          <select data-wb-anbieter-land aria-label="Impressum">
            <option value="">alle Anbieter</option>
            <option value="de">nur mit deutschem Impressum</option>
            <option value="nichtde">Ausland oder kein Impressum</option>
            <option value="ungeprueft">Impressum noch nicht geprüft</option>
            <option value="inaktiv">stillgelegte</option>
          </select>
          <input type="search" data-wb-anbieter-suche placeholder="Suche in Domain, Name, Betreiber …" aria-label="Suche">
        </div>
        <div data-wb-anbieter></div>
      </div>`;

    zeichneFaelle();
    host!.querySelector("[data-wb-filter-einstufung]")?.addEventListener("change", zeichneFaelle);
    host!.querySelector("[data-wb-filter-status]")?.addEventListener("change", zeichneFaelle);
    host!.querySelector("[data-wb-suche]")?.addEventListener("input", zeichneFaelle);
    host!.querySelector("[data-wb-anbieter-land]")?.addEventListener("change", zeichneAnbieter);
    host!.querySelector("[data-wb-anbieter-suche]")?.addEventListener("input", zeichneAnbieter);
    host!.querySelectorAll<HTMLElement>("[data-wb-ansicht]").forEach((b) => {
      b.addEventListener("click", () => { ansicht = b.dataset.wbAnsicht as typeof ansicht; zeichneAnsicht(); });
    });
    zeichneAnsicht();
    void anbieterLaden();
  }

  document.addEventListener("click", async (ev) => {
    const btn = (ev.target as HTMLElement).closest<HTMLButtonElement>('button[data-act="vorlage"]');
    if (!btn) return;
    const fall = btn.dataset.fall!;
    btn.disabled = true; const alt = btn.textContent; btn.textContent = "lädt …";
    const r = await deps.api<{ text?: string; error?: string; detail?: string }>(
      `/api/marketing/wettbewerb/vorlage?fall=${encodeURIComponent(fall)}`);
    btn.disabled = false; btn.textContent = alt ?? "Einreichungstext";
    const text = r.data?.text;
    if (r.status !== 200 || !text) {
      alert(`Konnte den Text nicht erzeugen (Fehler ${r.status}).${r.data?.detail ? `\n\n${r.data.detail}` : ""}`);
      return;
    }
    const w = window.open("", "_blank");
    if (w) { w.document.write(`<pre style="font:13px/1.5 ui-monospace,monospace;padding:24px;white-space:pre-wrap">${esc(text)}</pre>`); w.document.title = fall; }
  });

  // Frist eintragen: Datum aus dem Schreiben der Zentrale + Abstand für die Nachprüfung.
  document.addEventListener("click", (ev) => {
    const btn = (ev.target as HTMLElement).closest<HTMLButtonElement>('button[data-act="frist"]');
    if (!btn) return;
    const karte = btn.closest(".om-wb-fall");
    if (karte?.querySelector(".om-wb-fristform")) { karte.querySelector(".om-wb-fristform")!.remove(); return; }
    const kasten = document.createElement("div");
    kasten.className = "om-wb-fristform";
    kasten.innerHTML = `
      <label>Frist der Wettbewerbszentrale <input type="date" data-f="frist"></label>
      <label>Nachprüfung <input type="number" data-f="abstand" value="3" min="0" max="90" style="width:4rem"> Tage danach</label>
      <label>Notiz <input type="text" data-f="notiz" placeholder="z. B. Schreiben vom 12.08." style="width:14rem"></label>
      <button class="btn-secondary" data-f="speichern">Speichern</button>
      <span class="dim" data-f="hinweis"></span>`;
    karte?.appendChild(kasten);
    kasten.querySelector('[data-f="speichern"]')!.addEventListener("click", () => {
      void (async () => {
        const frist = (kasten.querySelector('[data-f="frist"]') as HTMLInputElement).value;
        const abstand = Number((kasten.querySelector('[data-f="abstand"]') as HTMLInputElement).value || 3);
        const notiz = (kasten.querySelector('[data-f="notiz"]') as HTMLInputElement).value;
        const hinweis = kasten.querySelector('[data-f="hinweis"]') as HTMLElement;
        if (!frist) { hinweis.textContent = "Bitte ein Datum wählen."; return; }
        hinweis.textContent = "Speichere …";
        const r = await deps.api<{ frist_pruefung_am?: string }>("/api/marketing/wettbewerb/frist", "POST",
          { fall: btn.dataset.fall, frist_am: frist, abstand_tage: abstand, notiz });
        hinweis.textContent = r.status === 200
          ? `Gespeichert — Nachprüfung am ${r.data?.frist_pruefung_am}.`
          : `Fehler ${r.status}`;
        if (r.status === 200) setTimeout(() => { void laden(); }, 900);
      })();
    });
  });

  document.addEventListener("click", async (ev) => {
    const btn = (ev.target as HTMLElement).closest<HTMLButtonElement>('button[data-act="anschreiben"], button[data-act="dossier-brief"]');
    if (!btn) return;
    const istDossier = btn.dataset.act === "dossier-brief";
    const abfrage = istDossier
      ? `/api/marketing/wettbewerb/dossier-anschreiben?domain=${encodeURIComponent(btn.dataset.domain!)}`
      : `/api/marketing/wettbewerb/anschreiben?fall=${encodeURIComponent(btn.dataset.fall!)}`;
    const alt = btn.textContent;
    btn.disabled = true; btn.textContent = "lädt …";
    const r = await deps.api<{ betreff?: string; text?: string }>(abfrage);
    btn.disabled = false;
    if (r.status !== 200 || !r.data?.text) { btn.textContent = alt ?? "Anschreiben"; alert(`Fehler ${r.status}`); return; }
    const ganz = `Betreff: ${r.data.betreff}\n\n${r.data.text}`;
    btn.textContent = alt ?? "Anschreiben";

    // Der Text wird IMMER sichtbar eingeblendet — ein Popup-Fenster wuerde geblockt und die
    // Zwischenablage setzt HTTPS voraus. So kommt Uli in jedem Fall an den Text.
    const fallKarte = btn.closest(".om-wb-fall");
    fallKarte?.querySelector(".om-wb-brief")?.remove();
    const kasten = document.createElement("div");
    kasten.className = "om-wb-brief";
    kasten.innerHTML = `<textarea readonly rows="16" style="width:100%;font:12px/1.5 ui-monospace,monospace;
      padding:10px;margin-top:8px;border:1px solid #bbb;border-radius:4px"></textarea>
      <div style="display:flex;gap:10px;align-items:center;margin-top:4px">
        <button class="btn-link" data-act="brief-kopieren">In die Zwischenablage</button>
        <button class="btn-link" data-act="brief-zu">schließen</button>
        <span class="dim" data-brief-status></span>
      </div>`;
    const feld = kasten.querySelector("textarea") as HTMLTextAreaElement;
    feld.value = ganz;
    fallKarte?.appendChild(kasten);
    feld.focus(); feld.select();

    const status = kasten.querySelector("[data-brief-status]") as HTMLElement;
    kasten.querySelector('[data-act="brief-kopieren"]')?.addEventListener("click", async () => {
      feld.focus(); feld.select();
      let ok = false;
      try { await navigator.clipboard.writeText(ganz); ok = true; } catch { /* Notloesung unten */ }
      if (!ok) { try { ok = document.execCommand("copy"); } catch { ok = false; } }
      status.textContent = ok ? "kopiert ✓" : "Bitte mit Strg+C kopieren — der Text ist markiert.";
    });
    kasten.querySelector('[data-act="brief-zu"]')?.addEventListener("click", () => kasten.remove());
  });

  document.addEventListener("change", async (ev) => {
    const sel = (ev.target as HTMLElement).closest<HTMLSelectElement>('select[data-act="status"]');
    if (!sel || !sel.value) return;
    const fall = sel.dataset.fall!;
    const notiz = prompt(`Notiz zur Statusänderung (${fall}) — optional:`) ?? "";
    const r = await deps.api("/api/marketing/wettbewerb/status", "POST", { fall, status: sel.value, notiz });
    if (r.status === 200) void laden(); else alert(`Status konnte nicht gesetzt werden (Fehler ${r.status}).`);
  });

  // Neuen Wettbewerber aufnehmen. Uli fügt hier meist die Adresse aus der Adresszeile ein;
  // die Domain wird serverseitig herausgelöst, damit dieselbe Seite nicht doppelt landet.
  document.addEventListener("submit", async (ev) => {
    const form = (ev.target as HTMLElement).closest<HTMLFormElement>("[data-wb-neu]");
    if (!form) return;
    ev.preventDefault();
    const status = form.querySelector("[data-wb-neu-status]") as HTMLElement;
    const knopf = form.querySelector("button") as HTMLButtonElement;
    const domain = (form.elements.namedItem("domain") as HTMLInputElement).value;
    const notiz = (form.elements.namedItem("notiz") as HTMLInputElement).value;
    knopf.disabled = true; status.textContent = "wird aufgenommen …";
    const r = await deps.api<{ ok?: boolean; domain?: string; bereitsVorhanden?: boolean; reaktiviert?: boolean; error?: string }>(
      "/api/marketing/wettbewerb/wettbewerber", "POST", { domain, notiz });
    knopf.disabled = false;
    if (r.status !== 200 || !r.data?.ok) {
      status.textContent = r.data?.error === "domain_ungueltig"
        ? "Das sieht nicht nach einer Adresse aus." : `Fehler ${r.status}`;
      return;
    }
    form.reset();
    status.textContent = r.data.bereitsVorhanden
      ? (r.data.reaktiviert ? `${r.data.domain} war stillgelegt und ist wieder aufgenommen.` : `${r.data.domain} steht bereits in der Liste.`)
      : `${r.data.domain} aufgenommen — Seiten werden im Hintergrund eingesammelt.`;
    if (!r.data.bereitsVorhanden) await deps.api("/api/marketing/wettbewerb/wettbewerber/scan", "POST", { domain: r.data.domain });
    await anbieterLaden();
  });

  document.addEventListener("click", async (ev) => {
    const t = ev.target as HTMLElement;
    const scan = t.closest<HTMLButtonElement>('button[data-act="wb-scan"]');
    if (scan) {
      scan.disabled = true; const alt = scan.textContent; scan.textContent = "läuft …";
      await deps.api("/api/marketing/wettbewerb/wettbewerber/scan", "POST", { domain: scan.dataset.domain });
      // Der Durchgang läuft losgelöst weiter; der Knopf kommt nach kurzer Rückmeldung zurück.
      setTimeout(() => { scan.disabled = false; scan.textContent = alt ?? "prüfen"; }, 2500);
      return;
    }
    const akt = t.closest<HTMLButtonElement>('button[data-act="wb-aktiv"]');
    if (!akt) return;
    akt.disabled = true;
    await deps.api("/api/marketing/wettbewerb/wettbewerber/aktiv", "POST",
      { id: Number(akt.dataset.id), aktiv: akt.dataset.aktiv === "1" });
    await anbieterLaden();
  });

  // Tab-Klick und Aktualisieren-Knopf delegiert abfangen
  document.addEventListener("click", (ev) => {
    const t = ev.target as HTMLElement;
    if (t.closest('[data-tab="wettbewerb"]')) { setTimeout(() => { if (finden()) void laden(); }, 40); return; }
    if (t.closest('[data-action="wb-neu-laden"]')) { if (finden()) void laden(); }
  });
}
